import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import ErrorBoundary from '../Common/ErrorBoundary';
import { mockWebinars } from '../../data/webinarData';

const {
  FiPlus, FiCalendar, FiUsers, FiBarChart2, FiEdit3, FiTrash2,
  FiDownload, FiRefreshCw, FiZap, FiCheck, FiX, FiInfo, FiMail,
  FiTrendingUp, FiActivity, FiArrowLeft, FiClock,
  FiTag, FiUser, FiAlignLeft, FiRepeat, FiBookOpen, FiGlobe,
  FiVideo, FiRadio, FiKey, FiLink, FiToggleLeft, FiToggleRight,
  FiMonitor, FiWifi, FiInbox,
} = FiIcons;

/* ─────────────────────────────────────────────────────────────────────────
   DATA SAFETY HELPERS
   Defined BEFORE static data so module-scope helpers never reference
   constants that haven't been initialised yet.
───────────────────────────────────────────────────────────────────────── */

const safeWebinars = (raw) => {
  if (!Array.isArray(raw)) return [];
  return raw.filter((w) => w && typeof w === 'object' && w.id != null);
};

const safeStr = (val, fallback = '—') =>
  typeof val === 'string' && val.trim() ? val.trim() : fallback;

const safeNum = (val) =>
  Number.isFinite(Number(val)) ? Number(val) : 0;

/* ─────────────────────────────────────────────────────────────────────────
   STATIC DATA
   Defined AFTER helpers so helper functions can safely reference these
   constants if they are ever inlined here in future.
───────────────────────────────────────────────────────────────────────── */
const MOCK_PROPOSALS = [
  {
    id: 'prop-1',
    name: 'Dr. Aris Thorne',
    email: 'aris@university.edu',
    title: 'The Neuroscience of Nostalgia',
    topic: 'Wellness',
    date: '2024-05-15',
    time: '02:00 PM',
    bio: 'Researcher specializing in memory and emotional processing.',
    description: 'A look at how our brains process legacy and the healing power of shared storytelling.',
  },
  {
    id: 'prop-2',
    name: 'Sarah Jenkins',
    email: 'sarah.j@legalheir.com',
    title: 'Modern Wills in a Digital Age',
    topic: 'Legal',
    date: '2024-05-22',
    time: '11:00 AM',
    bio: 'Estate attorney with 15 years experience in digital assets.',
    description: 'Protecting your social media, crypto, and digital memories for the next generation.',
  },
];

const CATEGORIES      = ['Writing', 'Legal', 'Wellness', 'Planning', 'Technology', 'Finance', 'Community'];
const RECURRENCE_OPTIONS = ['None', 'Daily', 'Weekly', 'Bi-weekly', 'Monthly'];
const DURATION_OPTIONS   = ['30 min', '45 min', '60 min', '90 min', '120 min'];

const WEBINAR_PLATFORMS = [
  { id: 'none',   label: 'None',        icon: FiMonitor },
  { id: 'zoom',   label: 'Zoom',        icon: FiVideo   },
  { id: 'meet',   label: 'Google Meet', icon: FiVideo   },
  { id: 'teams',  label: 'MS Teams',    icon: FiVideo   },
  { id: 'jitsi',  label: 'Jitsi',       icon: FiWifi    },
  { id: 'native', label: 'Native',      icon: FiRadio   },
];

const STREAMING_PLATFORMS = [
  { id: 'youtube',  label: 'YouTube Live',  color: 'bg-red-500',    textColor: 'text-red-600',    borderColor: 'border-red-200',    bgLight: 'bg-red-50'    },
  { id: 'facebook', label: 'Facebook Live', color: 'bg-blue-600',   textColor: 'text-blue-600',   borderColor: 'border-blue-200',   bgLight: 'bg-blue-50'   },
  { id: 'twitch',   label: 'Twitch',        color: 'bg-purple-600', textColor: 'text-purple-600', borderColor: 'border-purple-200', bgLight: 'bg-purple-50' },
];

const WEBINAR_CREDENTIAL_FIELDS = {
  zoom:   [
    { key: 'apiKey',       label: 'API Key',            placeholder: 'Zoom API Key',                         type: 'text'     },
    { key: 'apiSecret',    label: 'API Secret',         placeholder: 'Zoom API Secret',                      type: 'password' },
    { key: 'meetingId',    label: 'Meeting ID',         placeholder: 'e.g. 123 456 7890',                    type: 'text'     },
    { key: 'passcode',     label: 'Passcode',           placeholder: 'Meeting passcode',                     type: 'password' },
  ],
  meet:   [
    { key: 'meetUrl',      label: 'Meet URL',           placeholder: 'https://meet.google.com/xxx-xxxx-xxx', type: 'url'      },
    { key: 'clientId',     label: 'OAuth Client ID',    placeholder: 'Google OAuth Client ID',               type: 'text'     },
    { key: 'clientSecret', label: 'OAuth Secret',       placeholder: 'Google OAuth Secret',                  type: 'password' },
  ],
  teams:  [
    { key: 'tenantId',     label: 'Tenant ID',          placeholder: 'Azure Tenant ID',                      type: 'text'     },
    { key: 'clientId',     label: 'Client ID',          placeholder: 'Azure App Client ID',                  type: 'text'     },
    { key: 'clientSecret', label: 'Client Secret',      placeholder: 'Azure Client Secret',                  type: 'password' },
    { key: 'meetingUrl',   label: 'Meeting URL',        placeholder: 'Teams meeting link',                   type: 'url'      },
  ],
  jitsi:  [
    { key: 'serverUrl',    label: 'Server URL',         placeholder: 'https://meet.jit.si',                  type: 'url'      },
    { key: 'roomName',     label: 'Room Name',          placeholder: 'my-webinar-room',                      type: 'text'     },
    { key: 'appId',        label: 'App ID',             placeholder: 'Jitsi App ID (optional)',              type: 'text'     },
    { key: 'jwt',          label: 'JWT Token',          placeholder: 'JWT for private rooms (optional)',     type: 'password' },
  ],
  native: [
    { key: 'streamKey',    label: 'Stream Key',         placeholder: 'Internal stream key',                  type: 'password' },
    { key: 'rtmpUrl',      label: 'RTMP URL',           placeholder: 'rtmp://your-server/live',              type: 'url'      },
  ],
};

const STREAMING_CREDENTIAL_FIELDS = {
  youtube:  [
    { key: 'streamKey',   label: 'Stream Key',         placeholder: 'YouTube live stream key', type: 'password' },
    { key: 'broadcastId', label: 'Broadcast ID',       placeholder: 'YouTube broadcast ID',    type: 'text'     },
  ],
  facebook: [
    { key: 'pageToken',   label: 'Page Access Token',  placeholder: 'Facebook page token',     type: 'password' },
    { key: 'streamKey',   label: 'Stream Key',         placeholder: 'Facebook stream key',     type: 'password' },
  ],
  twitch:   [
    { key: 'streamKey',   label: 'Stream Key',         placeholder: 'Twitch stream key',       type: 'password' },
    { key: 'clientId',    label: 'Client ID',          placeholder: 'Twitch app client ID',    type: 'text'     },
  ],
};

/* Defined AFTER WEBINAR_PLATFORMS / STREAMING_PLATFORMS so the lookup
   arrays are guaranteed to exist at the point of the call.             */
const resolvePlatformLabel = (platformId) =>
  WEBINAR_PLATFORMS.find((p) => p.id === platformId)?.label ?? platformId ?? 'Unknown';

const resolveStreamingPlatform = (id) =>
  STREAMING_PLATFORMS.find((p) => p.id === id) ?? null;

const EMPTY_FORM = {
  title: '', seriesTitle: '', speaker: '', speakerBio: '',
  description: '', date: '', time: '', duration: '60 min',
  category: 'Wellness', recurrencePattern: 'None',
  isRecurring: false, location: 'Virtual', image: '',
};

const EMPTY_WEBINAR_CREDS = {
  apiKey: '', apiSecret: '', meetingId: '', passcode: '',
  meetUrl: '', clientId: '', clientSecret: '', tenantId: '',
  meetingUrl: '', serverUrl: '', roomName: '', appId: '',
  jwt: '', streamKey: '', rtmpUrl: '',
};
const EMPTY_STREAM_CREDS = { streamKey: '', broadcastId: '', pageToken: '', clientId: '' };

/* ─────────────────────────────────────────────────────────────────────────
   EMPTY STATES
───────────────────────────────────────────────────────────────────────── */
const EmptyWebinarList = () => (
  <div className="py-20 text-center">
    <SafeIcon icon={FiInbox} className="w-16 h-16 text-brand-dark mx-auto mb-4" />
    <p className="text-text-muted font-light italic text-sm">No webinar sessions scheduled yet.</p>
    <p className="text-text-muted/60 text-xs mt-1">Click "New Series" to add the first session.</p>
  </div>
);

const EmptyProposalList = () => (
  <div className="py-20 text-center">
    <SafeIcon icon={FiCheck} className="w-16 h-16 text-green-500/20 mx-auto mb-4" />
    <p className="text-text-muted font-light italic">No pending proposals to review.</p>
  </div>
);

/* ─────────────────────────────────────────────────────────────────────────
   ADD SERIES MODAL
   Fix: isMountedRef guards every post-async state update so that if the
   modal is closed (unmounted) while the fake save delay is in-flight,
   no setState calls fire on the dead component.
───────────────────────────────────────────────────────────────────────── */
const AddSeriesModal = ({ onClose, onSave }) => {
  const [form, setForm]     = useState(EMPTY_FORM);
  const [step, setStep]     = useState(1);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  const [selectedWebinarPlatform, setSelectedWebinarPlatform]       = useState('none');
  const [webinarCredentials, setWebinarCredentials]                 = useState({ ...EMPTY_WEBINAR_CREDS });
  const [selectedStreamingPlatforms, setSelectedStreamingPlatforms] = useState([]);
  const [streamingCredentials, setStreamingCredentials]             = useState({
    youtube:  { ...EMPTY_STREAM_CREDS },
    facebook: { ...EMPTY_STREAM_CREDS },
    twitch:   { ...EMPTY_STREAM_CREDS },
  });

  /**
   * isMountedRef — set to false in the cleanup of the mount effect.
   * Every async callback checks this before calling setState or invoking
   * parent callbacks, preventing the React "can't perform state update on
   * an unmounted component" warning and any associated crashes.
   */
  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  /**
   * saveTimerRef — holds the ID of the in-flight setTimeout so it can be
   * cancelled in the cleanup if the modal unmounts before it fires.
   */
  const saveTimerRef = useRef(null);
  useEffect(() => {
    return () => {
      if (saveTimerRef.current != null) {
        clearTimeout(saveTimerRef.current);
      }
    };
  }, []);

  const set = useCallback((key, val) => {
    setForm(prev => ({
      ...prev,
      [key]: val,
      ...(key === 'recurrencePattern' ? { isRecurring: val !== 'None' } : {}),
    }));
    if (errors[key]) setErrors(prev => ({ ...prev, [key]: null }));
  }, [errors]);

  const setWebinarCred = useCallback(
    (key, val) => setWebinarCredentials(prev => ({ ...prev, [key]: val })),
    []
  );

  const setStreamingCred = useCallback((platformId, key, val) =>
    setStreamingCredentials(prev => ({
      ...prev,
      [platformId]: { ...(prev[platformId] ?? {}), [key]: val },
    })),
    []
  );

  const toggleStreamingPlatform = useCallback((id) =>
    setSelectedStreamingPlatforms(prev =>
      prev.includes(id) ? prev.filter(p => p !== id) : [...prev, id]
    ),
    []
  );

  const validateStep1 = () => {
    const e = {};
    if (!form.title.trim())       e.title       = 'Session title is required';
    if (!form.speaker.trim())     e.speaker     = 'Speaker name is required';
    if (!form.description.trim()) e.description = 'Description is required';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const validateStep2 = () => {
    const e = {};
    if (!form.date) e.date = 'Date is required';
    if (!form.time) e.time = 'Time is required';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleNext = () => {
    if (step === 1 && validateStep1()) setStep(2);
    else if (step === 2 && validateStep2()) setStep(3);
    else if (step === 3) setStep(4);
  };

  const handleSave = useCallback(() => {
    if (!isMountedRef.current) return;
    setSaving(true);

    saveTimerRef.current = setTimeout(() => {
      /* Guard: modal may have been force-closed during the delay */
      if (!isMountedRef.current) return;

      const credFields = WEBINAR_CREDENTIAL_FIELDS[selectedWebinarPlatform];
      const platformConfig =
        selectedWebinarPlatform !== 'none' && Array.isArray(credFields)
          ? {
              platform: selectedWebinarPlatform,
              credentials: Object.fromEntries(
                credFields.map(f => [f.key, webinarCredentials[f.key] ?? ''])
              ),
            }
          : null;

      const streamingConfig = selectedStreamingPlatforms
        .map(id => {
          const fields = STREAMING_CREDENTIAL_FIELDS[id];
          if (!Array.isArray(fields)) return null;
          return {
            platform: id,
            credentials: Object.fromEntries(
              fields.map(f => [f.key, streamingCredentials[id]?.[f.key] ?? ''])
            ),
          };
        })
        .filter(Boolean);

      onSave({
        id: `web-${Date.now()}`,
        ...form,
        registrants: 0,
        isLive: false,
        image:
          form.image ||
          'https://images.unsplash.com/photo-1475721027785-f74eccf877e2?q=80&w=2070',
        platformConfig,
        streamingConfig,
      });

      /* setSaving after onSave so parent state settles first */
      if (isMountedRef.current) setSaving(false);
      onClose();
    }, 800);
  }, [
    form, selectedWebinarPlatform, webinarCredentials,
    selectedStreamingPlatforms, streamingCredentials,
    onSave, onClose,
  ]);

  const inputClass = (field) =>
    `w-full bg-brand-light/60 border rounded-2xl px-5 py-3.5 text-sm text-accent-navy placeholder-text-muted/50 focus:outline-none focus:ring-2 focus:ring-accent-gold/40 transition-all ${
      errors[field]
        ? 'border-red-400 focus:ring-red-200'
        : 'border-brand-dark focus:border-accent-gold'
    }`;

  const credInputClass =
    'w-full bg-white border border-brand-dark rounded-xl px-4 py-3 text-sm text-accent-navy placeholder-text-muted/40 focus:outline-none focus:ring-2 focus:ring-accent-gold/40 focus:border-accent-gold transition-all';

  const steps = ['Details', 'Schedule', 'Platforms', 'Preview'];

  const resolvedStreamingSelections = useMemo(
    () => selectedStreamingPlatforms.map(resolveStreamingPlatform).filter(Boolean),
    [selectedStreamingPlatforms]
  );

  return (
    <div className="fixed inset-0 z-[300] flex items-center justify-center p-4 bg-accent-navy/60 backdrop-blur-md">
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 24 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 24 }}
        className="bg-white rounded-[40px] shadow-2xl w-full max-w-2xl overflow-hidden border border-brand-dark flex flex-col max-h-[92vh]"
      >
        {/* Header */}
        <div className="p-8 border-b border-brand-light bg-accent-navy shrink-0">
          <div className="flex justify-between items-start mb-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-accent-gold/20 text-accent-gold rounded-2xl flex items-center justify-center border border-accent-gold/30">
                <SafeIcon icon={FiBookOpen} className="w-6 h-6" />
              </div>
              <div>
                <h3 className="text-2xl font-serif text-white">New Webinar Series</h3>
                <p className="text-[10px] font-black uppercase tracking-widest text-white/50">
                  Step {step} of {steps.length} — {steps[step - 1]}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              disabled={saving}
              className="p-2 hover:bg-white/10 rounded-full text-white/60 hover:text-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <SafeIcon icon={FiX} className="w-5 h-5" />
            </button>
          </div>
          <div className="flex gap-2">
            {steps.map((s, i) => (
              <div key={s} className="flex-1 flex flex-col gap-1.5">
                <div
                  className={`h-1 rounded-full transition-all duration-500 ${
                    i + 1 <= step ? 'bg-accent-gold' : 'bg-white/10'
                  }`}
                />
                <span
                  className={`text-[9px] font-black uppercase tracking-widest ${
                    i + 1 <= step ? 'text-accent-gold' : 'text-white/30'
                  }`}
                >
                  {s}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Body */}
        <div className="flex-grow overflow-y-auto p-8 min-h-0">
          <AnimatePresence mode="wait">

            {/* Step 1 */}
            {step === 1 && (
              <motion.div key="step1" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-5">
                <div>
                  <label className="block text-[10px] font-black uppercase tracking-widest text-accent-navy mb-2">
                    <SafeIcon icon={FiBookOpen} className="inline mr-1.5" /> Session Title *
                  </label>
                  <input className={inputClass('title')} placeholder="e.g. The Art of Legacy Writing" value={form.title} onChange={e => set('title', e.target.value)} />
                  {errors.title && <p className="text-red-500 text-[10px] mt-1 font-bold">{errors.title}</p>}
                </div>
                <div>
                  <label className="block text-[10px] font-black uppercase tracking-widest text-accent-navy mb-2">
                    <SafeIcon icon={FiRepeat} className="inline mr-1.5" /> Series Name
                  </label>
                  <input className={inputClass('seriesTitle')} placeholder="e.g. Legacy Builders Series (optional)" value={form.seriesTitle} onChange={e => set('seriesTitle', e.target.value)} />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-[10px] font-black uppercase tracking-widest text-accent-navy mb-2">
                      <SafeIcon icon={FiTag} className="inline mr-1.5" /> Category
                    </label>
                    <select className={inputClass('category')} value={form.category} onChange={e => set('category', e.target.value)}>
                      {CATEGORIES.map(c => <option key={c}>{c}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-[10px] font-black uppercase tracking-widest text-accent-navy mb-2">
                      <SafeIcon icon={FiGlobe} className="inline mr-1.5" /> Location
                    </label>
                    <input className={inputClass('location')} placeholder="Virtual / City" value={form.location} onChange={e => set('location', e.target.value)} />
                  </div>
                </div>
                <div>
                  <label className="block text-[10px] font-black uppercase tracking-widest text-accent-navy mb-2">
                    <SafeIcon icon={FiUser} className="inline mr-1.5" /> Speaker Name *
                  </label>
                  <input className={inputClass('speaker')} placeholder="e.g. Dr. Julian Thorne" value={form.speaker} onChange={e => set('speaker', e.target.value)} />
                  {errors.speaker && <p className="text-red-500 text-[10px] mt-1 font-bold">{errors.speaker}</p>}
                </div>
                <div>
                  <label className="block text-[10px] font-black uppercase tracking-widest text-accent-navy mb-2">Speaker Bio</label>
                  <textarea className={`${inputClass('speakerBio')} resize-none`} rows={2} placeholder="Brief bio about the speaker…" value={form.speakerBio} onChange={e => set('speakerBio', e.target.value)} />
                </div>
                <div>
                  <label className="block text-[10px] font-black uppercase tracking-widest text-accent-navy mb-2">
                    <SafeIcon icon={FiAlignLeft} className="inline mr-1.5" /> Session Description *
                  </label>
                  <textarea className={`${inputClass('description')} resize-none`} rows={3} placeholder="What will attendees learn or experience?" value={form.description} onChange={e => set('description', e.target.value)} />
                  {errors.description && <p className="text-red-500 text-[10px] mt-1 font-bold">{errors.description}</p>}
                </div>
                <div>
                  <label className="block text-[10px] font-black uppercase tracking-widest text-accent-navy mb-2">Cover Image URL</label>
                  <input className={inputClass('image')} placeholder="https://images.unsplash.com/…" value={form.image} onChange={e => set('image', e.target.value)} />
                </div>
              </motion.div>
            )}

            {/* Step 2 */}
            {step === 2 && (
              <motion.div key="step2" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-5">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-[10px] font-black uppercase tracking-widest text-accent-navy mb-2">
                      <SafeIcon icon={FiCalendar} className="inline mr-1.5" /> Date *
                    </label>
                    <input type="date" className={inputClass('date')} value={form.date} onChange={e => set('date', e.target.value)} />
                    {errors.date && <p className="text-red-500 text-[10px] mt-1 font-bold">{errors.date}</p>}
                  </div>
                  <div>
                    <label className="block text-[10px] font-black uppercase tracking-widest text-accent-navy mb-2">
                      <SafeIcon icon={FiClock} className="inline mr-1.5" /> Time *
                    </label>
                    <input type="time" className={inputClass('time')} value={form.time} onChange={e => set('time', e.target.value)} />
                    {errors.time && <p className="text-red-500 text-[10px] mt-1 font-bold">{errors.time}</p>}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-[10px] font-black uppercase tracking-widest text-accent-navy mb-2">
                      <SafeIcon icon={FiClock} className="inline mr-1.5" /> Duration
                    </label>
                    <select className={inputClass('duration')} value={form.duration} onChange={e => set('duration', e.target.value)}>
                      {DURATION_OPTIONS.map(d => <option key={d}>{d}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-[10px] font-black uppercase tracking-widest text-accent-navy mb-2">
                      <SafeIcon icon={FiRepeat} className="inline mr-1.5" /> Recurrence
                    </label>
                    <select className={inputClass('recurrencePattern')} value={form.recurrencePattern} onChange={e => set('recurrencePattern', e.target.value)}>
                      {RECURRENCE_OPTIONS.map(r => <option key={r}>{r}</option>)}
                    </select>
                  </div>
                </div>
                {form.isRecurring && (
                  <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} className="bg-purple-50 border border-purple-200 rounded-2xl p-4 flex items-start gap-3">
                    <SafeIcon icon={FiRefreshCw} className="text-purple-500 mt-0.5 shrink-0" />
                    <div>
                      <p className="text-[10px] font-black uppercase tracking-widest text-purple-700 mb-0.5">Recurring Series Enabled</p>
                      <p className="text-xs text-purple-600 font-light">
                        This session will repeat <strong>{form.recurrencePattern.toLowerCase()}</strong> starting from the selected date.
                      </p>
                    </div>
                  </motion.div>
                )}
                <div className="bg-brand-light/60 border border-brand-dark rounded-2xl p-5 space-y-3">
                  <p className="text-[10px] font-black uppercase tracking-widest text-accent-navy">Session Summary</p>
                  <div className="grid grid-cols-2 gap-x-8 gap-y-2 text-xs text-text-muted">
                    <span><strong className="text-accent-navy">Title:</strong> {safeStr(form.title, 'Untitled')}</span>
                    <span><strong className="text-accent-navy">Speaker:</strong> {safeStr(form.speaker, 'TBC')}</span>
                    <span><strong className="text-accent-navy">Category:</strong> {safeStr(form.category)}</span>
                    <span><strong className="text-accent-navy">Location:</strong> {safeStr(form.location)}</span>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Step 3 */}
            {step === 3 && (
              <motion.div key="step3" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-8">
                <div>
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-8 h-8 bg-accent-navy/10 rounded-xl flex items-center justify-center">
                      <SafeIcon icon={FiVideo} className="text-accent-navy w-4 h-4" />
                    </div>
                    <div>
                      <p className="text-[10px] font-black uppercase tracking-widest text-accent-navy">Webinar Integration</p>
                      <p className="text-[9px] text-text-muted">Select the platform hosting this webinar</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-2 mb-4">
                    {WEBINAR_PLATFORMS.map(p => (
                      <button
                        key={p.id}
                        onClick={() => setSelectedWebinarPlatform(p.id)}
                        className={`flex items-center justify-center gap-2 px-4 py-3 rounded-2xl border text-[10px] font-black uppercase tracking-widest transition-all ${
                          selectedWebinarPlatform === p.id
                            ? 'bg-accent-navy text-white border-accent-navy shadow-lg shadow-accent-navy/20'
                            : 'bg-brand-light/60 text-accent-navy border-brand-dark hover:border-accent-navy/40'
                        }`}
                      >
                        <SafeIcon icon={p.icon} className="w-3.5 h-3.5" />
                        {p.label}
                      </button>
                    ))}
                  </div>
                  <AnimatePresence mode="wait">
                    {selectedWebinarPlatform !== 'none' &&
                      Array.isArray(WEBINAR_CREDENTIAL_FIELDS[selectedWebinarPlatform]) && (
                        <motion.div
                          key={selectedWebinarPlatform}
                          initial={{ opacity: 0, y: -8 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -8 }}
                          className="bg-brand-light/40 border border-brand-dark rounded-2xl p-5 space-y-4"
                        >
                          <div className="flex items-center gap-2 mb-2">
                            <SafeIcon icon={FiKey} className="text-accent-gold w-3.5 h-3.5" />
                            <p className="text-[9px] font-black uppercase tracking-widest text-accent-navy">
                              {resolvePlatformLabel(selectedWebinarPlatform)} Credentials
                            </p>
                          </div>
                          <div className="grid grid-cols-1 gap-3">
                            {WEBINAR_CREDENTIAL_FIELDS[selectedWebinarPlatform].map(field => (
                              <div key={field.key}>
                                <label className="block text-[9px] font-black uppercase tracking-widest text-text-muted mb-1.5">
                                  {field.label}
                                </label>
                                <input
                                  type={field.type}
                                  className={credInputClass}
                                  placeholder={field.placeholder}
                                  value={webinarCredentials[field.key] ?? ''}
                                  onChange={e => setWebinarCred(field.key, e.target.value)}
                                  autoComplete="off"
                                />
                              </div>
                            ))}
                          </div>
                          <div className="flex items-center gap-2 pt-2 border-t border-brand-dark/40">
                            <SafeIcon icon={FiInfo} className="text-text-muted w-3 h-3 shrink-0" />
                            <p className="text-[9px] text-text-muted">
                              Credentials are stored securely and used only to generate meeting links.
                            </p>
                          </div>
                        </motion.div>
                      )}
                  </AnimatePresence>
                </div>

                <div>
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-8 h-8 bg-red-50 rounded-xl flex items-center justify-center">
                      <SafeIcon icon={FiRadio} className="text-red-500 w-4 h-4" />
                    </div>
                    <div>
                      <p className="text-[10px] font-black uppercase tracking-widest text-accent-navy">Streaming Destinations</p>
                      <p className="text-[9px] text-text-muted">Simultaneously stream to multiple platforms</p>
                    </div>
                  </div>
                  <div className="space-y-3">
                    {STREAMING_PLATFORMS.map(platform => {
                      const isSelected = selectedStreamingPlatforms.includes(platform.id);
                      const credFields = STREAMING_CREDENTIAL_FIELDS[platform.id] ?? [];
                      return (
                        <div
                          key={platform.id}
                          className={`border rounded-2xl overflow-hidden transition-all ${
                            isSelected ? platform.borderColor : 'border-brand-dark'
                          }`}
                        >
                          <button
                            onClick={() => toggleStreamingPlatform(platform.id)}
                            className={`w-full flex items-center justify-between px-5 py-4 transition-all ${
                              isSelected ? platform.bgLight : 'bg-brand-light/40 hover:bg-brand-light'
                            }`}
                          >
                            <div className="flex items-center gap-3">
                              <div className={`w-7 h-7 ${platform.color} rounded-lg flex items-center justify-center`}>
                                <SafeIcon icon={FiRadio} className="text-white w-3.5 h-3.5" />
                              </div>
                              <span className={`text-[10px] font-black uppercase tracking-widest ${isSelected ? platform.textColor : 'text-accent-navy'}`}>
                                {platform.label}
                              </span>
                            </div>
                            <SafeIcon
                              icon={isSelected ? FiToggleRight : FiToggleLeft}
                              className={`w-6 h-6 transition-colors ${isSelected ? platform.textColor : 'text-text-muted'}`}
                            />
                          </button>
                          <AnimatePresence>
                            {isSelected && credFields.length > 0 && (
                              <motion.div
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: 'auto', opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                className="overflow-hidden"
                              >
                                <div className="px-5 pb-5 pt-3 space-y-3 bg-white border-t border-brand-dark/20">
                                  {credFields.map(field => (
                                    <div key={field.key}>
                                      <label className="block text-[9px] font-black uppercase tracking-widest text-text-muted mb-1.5">
                                        {field.label}
                                      </label>
                                      <input
                                        type={field.type}
                                        className={credInputClass}
                                        placeholder={field.placeholder}
                                        value={streamingCredentials[platform.id]?.[field.key] ?? ''}
                                        onChange={e => setStreamingCred(platform.id, field.key, e.target.value)}
                                        autoComplete="off"
                                      />
                                    </div>
                                  ))}
                                  <div className="flex items-center gap-2 pt-2 border-t border-brand-dark/20">
                                    <SafeIcon icon={FiLink} className="text-text-muted w-3 h-3 shrink-0" />
                                    <p className="text-[9px] text-text-muted">
                                      Stream will go live automatically when the session starts.
                                    </p>
                                  </div>
                                </div>
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </div>
                      );
                    })}
                  </div>
                  {selectedStreamingPlatforms.length === 0 && (
                    <p className="text-[9px] text-text-muted italic mt-3 text-center">
                      No streaming destinations selected — webinar will be hosted only on the chosen platform.
                    </p>
                  )}
                </div>
              </motion.div>
            )}

            {/* Step 4: Preview */}
            {step === 4 && (
              <motion.div key="step4" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-6">
                <p className="text-[10px] font-black uppercase tracking-widest text-text-muted">Review before publishing</p>
                <div className="rounded-3xl border border-brand-dark overflow-hidden shadow-sm">
                  <div
                    className="h-40 bg-cover bg-center relative"
                    style={{
                      backgroundImage: `url(${
                        form.image ||
                        'https://images.unsplash.com/photo-1475721027785-f74eccf877e2?q=80&w=2070'
                      })`,
                    }}
                  >
                    <div className="absolute inset-0 bg-accent-navy/60" />
                    <div className="absolute bottom-4 left-5 right-5">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="bg-accent-gold text-accent-navy px-2 py-0.5 rounded text-[8px] font-black uppercase tracking-widest">
                          {safeStr(form.category, 'General')}
                        </span>
                        {form.isRecurring && (
                          <span className="bg-purple-500/80 text-white px-2 py-0.5 rounded text-[8px] font-black uppercase tracking-widest flex items-center gap-1">
                            <SafeIcon icon={FiRefreshCw} /> {safeStr(form.recurrencePattern, 'Recurring')}
                          </span>
                        )}
                      </div>
                      <h4 className="text-white font-serif text-xl leading-tight">
                        {safeStr(form.title, 'Untitled Session')}
                      </h4>
                    </div>
                  </div>
                  <div className="p-6 space-y-4 bg-white">
                    <div className="flex flex-wrap gap-6 text-[10px] font-black uppercase tracking-widest text-text-muted">
                      <span className="flex items-center gap-1.5 text-accent-gold">
                        <SafeIcon icon={FiCalendar} /> {safeStr(form.date, 'TBD')} • {safeStr(form.time, 'TBD')}
                      </span>
                      <span className="flex items-center gap-1.5">
                        <SafeIcon icon={FiClock} /> {safeStr(form.duration, '60 min')}
                      </span>
                      <span className="flex items-center gap-1.5">
                        <SafeIcon icon={FiGlobe} /> {safeStr(form.location, 'Virtual')}
                      </span>
                    </div>
                    {form.seriesTitle && (
                      <p className="text-[10px] font-black uppercase tracking-widest text-purple-500">
                        {form.seriesTitle}
                      </p>
                    )}
                    <p className="text-sm text-text-muted font-light leading-relaxed">
                      {safeStr(form.description, 'No description provided.')}
                    </p>
                    <div className="pt-4 border-t border-brand-light flex items-start gap-3">
                      <div className="w-9 h-9 bg-accent-navy text-white rounded-xl flex items-center justify-center text-xs font-black shrink-0">
                        {safeStr(form.speaker, 'S')[0].toUpperCase()}
                      </div>
                      <div>
                        <p className="text-sm font-black text-accent-navy">{safeStr(form.speaker, 'Speaker Name')}</p>
                        <p className="text-xs text-text-muted font-light">{safeStr(form.speakerBio, 'No bio provided.')}</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-brand-light/50 border border-brand-dark rounded-2xl p-5 space-y-3">
                  <p className="text-[10px] font-black uppercase tracking-widest text-accent-navy mb-3">
                    Integration Summary
                  </p>
                  <div className="flex items-center gap-3">
                    <SafeIcon icon={FiVideo} className="text-accent-navy w-4 h-4 shrink-0" />
                    <div>
                      <p className="text-[9px] font-black uppercase tracking-widest text-text-muted">Webinar Platform</p>
                      <p className="text-sm font-bold text-accent-navy capitalize">
                        {selectedWebinarPlatform === 'none'
                          ? 'No integration'
                          : resolvePlatformLabel(selectedWebinarPlatform)}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <SafeIcon icon={FiRadio} className="text-red-500 w-4 h-4 shrink-0 mt-0.5" />
                    <div>
                      <p className="text-[9px] font-black uppercase tracking-widest text-text-muted">
                        Streaming Destinations
                      </p>
                      {resolvedStreamingSelections.length === 0 ? (
                        <p className="text-sm font-bold text-text-muted">None selected</p>
                      ) : (
                        <div className="flex flex-wrap gap-2 mt-1">
                          {resolvedStreamingSelections.map(sp => (
                            <span
                              key={sp.id}
                              className={`px-2 py-0.5 rounded text-[8px] font-black uppercase tracking-widest text-white ${sp.color}`}
                            >
                              {sp.label}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="bg-green-50 border border-green-200 rounded-2xl p-4 flex items-center gap-3">
                  <SafeIcon icon={FiCheck} className="text-green-500 shrink-0" />
                  <p className="text-xs text-green-700 font-medium">
                    This webinar will be added to the public schedule and visible to all registered users immediately after publishing.
                  </p>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-brand-light bg-brand-light/30 shrink-0 flex justify-between items-center gap-4">
          <button
            onClick={step === 1 ? onClose : () => setStep(s => s - 1)}
            disabled={saving}
            className="px-6 py-3 border border-brand-dark text-accent-navy rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-brand-light transition-all disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {step === 1 ? 'Cancel' : '← Back'}
          </button>
          {step < 4 ? (
            <button
              onClick={handleNext}
              className="btn-accent px-8 py-3 text-[10px] uppercase tracking-widest flex items-center gap-2"
            >
              Continue →
            </button>
          ) : (
            <button
              onClick={handleSave}
              disabled={saving}
              className="btn-accent px-10 py-3 text-[10px] uppercase tracking-widest flex items-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {saving ? (
                <><SafeIcon icon={FiRefreshCw} className="animate-spin" /> Publishing…</>
              ) : (
                <><SafeIcon icon={FiCheck} /> Publish Series</>
              )}
            </button>
          )}
        </div>
      </motion.div>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────────────────
   WEBINAR ROW
───────────────────────────────────────────────────────────────────────── */
const WebinarRow = ({ w, onViewAnalytics }) => {
  if (!w || typeof w !== 'object') return null;

  const title         = safeStr(w.title, 'Untitled Session');
  const date          = safeStr(w.date, '—');
  const time          = safeStr(w.time, '—');
  const registrants   = safeNum(w.registrants);
  const isRecurring   = Boolean(w.isRecurring);
  const platformId    = w.platformConfig?.platform;
  const platformLabel = platformId ? resolvePlatformLabel(platformId) : null;
  const streamCount   = Array.isArray(w.streamingConfig)
    ? w.streamingConfig.filter(Boolean).length
    : 0;

  return (
    <div className="p-8 hover:bg-brand-light/30 transition-colors flex flex-col lg:flex-row lg:items-center gap-8 group">
      <div className="flex-grow">
        <div className="flex items-center gap-3 mb-2 flex-wrap">
          <h5 className="text-xl font-serif text-accent-navy group-hover:text-accent-gold transition-colors">
            {title}
          </h5>
          {isRecurring && (
            <span className="bg-purple-50 text-purple-600 px-2 py-0.5 rounded text-[8px] font-black uppercase tracking-widest border border-purple-100 flex items-center gap-1">
              <SafeIcon icon={FiRefreshCw} /> Recurring
            </span>
          )}
          {platformLabel && (
            <span className="bg-accent-navy/10 text-accent-navy px-2 py-0.5 rounded text-[8px] font-black uppercase tracking-widest border border-accent-navy/20 flex items-center gap-1">
              <SafeIcon icon={FiVideo} /> {platformLabel}
            </span>
          )}
        </div>
        <div className="flex flex-wrap gap-6 text-[10px] font-black uppercase tracking-widest text-text-muted">
          <span className="flex items-center gap-1.5 text-accent-gold">
            <SafeIcon icon={FiCalendar} /> {date} • {time}
          </span>
          <span className="flex items-center gap-1.5">
            <SafeIcon icon={FiUsers} /> {registrants.toLocaleString()} Registered
          </span>
          {streamCount > 0 && (
            <span className="flex items-center gap-1.5 text-red-500">
              <SafeIcon icon={FiRadio} /> {streamCount} Stream{streamCount > 1 ? 's' : ''}
            </span>
          )}
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <button
          onClick={onViewAnalytics}
          className="p-3 bg-brand-light text-accent-navy rounded-xl hover:bg-accent-gold hover:text-white transition-all flex items-center gap-2"
        >
          <SafeIcon icon={FiBarChart2} className="w-4 h-4" />
          <span className="text-[10px] font-black uppercase tracking-widest hidden xl:inline">Report</span>
        </button>
        <button className="p-3 bg-brand-light text-accent-navy rounded-xl hover:bg-brand-dark transition-all">
          <SafeIcon icon={FiEdit3} className="w-4 h-4" />
        </button>
        <button className="p-3 bg-red-50 text-red-500 rounded-xl hover:bg-red-500 hover:text-white transition-all border border-red-100">
          <SafeIcon icon={FiTrash2} className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────────────────
   PROPOSAL CARD
───────────────────────────────────────────────────────────────────────── */
const ProposalCard = ({ prop, onApprove, onReject }) => {
  if (!prop || typeof prop !== 'object' || !prop.id) return null;

  const name        = safeStr(prop.name, 'Unknown Speaker');
  const email       = safeStr(prop.email, '—');
  const title       = safeStr(prop.title, 'Untitled Proposal');
  const topic       = safeStr(prop.topic, 'General');
  const date        = safeStr(prop.date, '—');
  const bio         = safeStr(prop.bio, 'No bio provided.');
  const description = safeStr(prop.description, 'No description provided.');

  return (
    <div className="bg-brand-light/30 border border-brand-dark rounded-3xl p-6 md:p-8 hover:border-accent-gold transition-all group">
      <div className="flex flex-col md:flex-row justify-between gap-6 mb-6">
        <div className="flex-grow">
          <span className="bg-accent-gold/10 text-accent-gold px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-widest mb-3 inline-block border border-accent-gold/20">
            {topic}
          </span>
          <h4 className="text-2xl font-serif text-accent-navy mb-2">{title}</h4>
          <div className="flex items-center gap-4 text-[10px] font-black uppercase tracking-widest text-text-muted">
            <span className="flex items-center gap-1.5"><SafeIcon icon={FiCalendar} /> {date}</span>
            <span className="flex items-center gap-1.5"><SafeIcon icon={FiMail} /> {email}</span>
          </div>
        </div>
        <div className="flex gap-2 shrink-0">
          <button
            onClick={() => onApprove(prop)}
            className="bg-green-500 text-white px-6 py-3 rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-green-600 transition-colors flex items-center gap-2 shadow-lg shadow-green-100"
          >
            <SafeIcon icon={FiCheck} /> Approve
          </button>
          <button
            onClick={() => onReject(prop.id)}
            className="bg-white border border-brand-dark text-red-500 px-6 py-3 rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-red-50 transition-colors flex items-center gap-2"
          >
            <SafeIcon icon={FiX} /> Reject
          </button>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 pt-6 border-t border-brand-dark/50">
        <div>
          <p className="text-[9px] font-black uppercase text-accent-navy mb-2">Speaker: {name}</p>
          <p className="text-xs text-text-muted font-light leading-relaxed italic">"{bio}"</p>
        </div>
        <div>
          <p className="text-[9px] font-black uppercase text-accent-navy mb-2">Session Concept</p>
          <p className="text-xs text-text-muted font-light leading-relaxed line-clamp-3">{description}</p>
        </div>
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────────────────
   ANALYTICS STAT CARD
───────────────────────────────────────────────────────────────────────── */
const AnalyticsStatCard = ({ label, value, sub, icon, color }) => (
  <div className="bg-white p-8 rounded-[32px] border border-brand-dark shadow-sm">
    <div className={`w-12 h-12 rounded-2xl bg-brand-light flex items-center justify-center mb-6 ${color}`}>
      <SafeIcon icon={icon} className="w-6 h-6" />
    </div>
    <h3 className="text-4xl font-serif text-accent-navy mb-1">{value ?? '—'}</h3>
    <p className="text-[10px] font-black uppercase tracking-widest text-text-muted mb-2">{label ?? ''}</p>
    <p className="text-[9px] font-bold text-green-500">{sub ?? ''}</p>
  </div>
);

/* ─────────────────────────────────────────────────────────────────────────
   PROGRESS ROW
   Fix: animate prop used instead of initial+animate so the bar does NOT
   re-animate from zero on every parent re-render (e.g. after approve/reject).
───────────────────────────────────────────────────────────────────────── */
const ProgressRow = ({ label, value, color }) => {
  const pct = safeNum(value);
  return (
    <div className="space-y-2">
      <div className="flex justify-between items-center text-[10px] font-black uppercase tracking-widest text-accent-navy">
        <span>{label}</span>
        <span>{pct}%</span>
      </div>
      <div className="h-1.5 bg-brand-light rounded-full overflow-hidden">
        <motion.div
          className={`h-full ${color}`}
          initial={{ width: 0 }}
          whileInView={{ width: `${pct}%` }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, ease: 'easeOut' }}
        />
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────────────────
   MAIN COMPONENT
───────────────────────────────────────────────────────────────────────── */
const AdminWebinarTabInner = () => {
  const [webinars, setWebinars]           = useState(() => safeWebinars(mockWebinars));
  const [proposals, setProposals]         = useState(() =>
    Array.isArray(MOCK_PROPOSALS) ? MOCK_PROPOSALS.filter(p => p && p.id) : []
  );
  const [view, setView]                   = useState('list');
  const [showProposals, setShowProposals] = useState(false);
  const [showAddModal, setShowAddModal]   = useState(false);

  /**
   * isMountedRef for the parent component — guards the handleApprove /
   * handleReject callbacks that are invoked from inside a modal whose
   * AnimatePresence exit animation may still be running when parent
   * state is updated.
   */
  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => { isMountedRef.current = false; };
  }, []);

  const handleApprove = useCallback((proposal) => {
    if (!isMountedRef.current || !proposal?.id) return;
    const newWebinar = {
      id:          `web-${Date.now()}`,
      title:       safeStr(proposal.title, 'Untitled'),
      seriesTitle: 'Community Spotlight',
      isRecurring: false,
      date:        safeStr(proposal.date),
      time:        safeStr(proposal.time),
      duration:    '60 min',
      speaker:     safeStr(proposal.name, 'Unknown'),
      speakerBio:  safeStr(proposal.bio),
      description: safeStr(proposal.description),
      image:       'https://images.unsplash.com/photo-1475721027785-f74eccf877e2?q=80&w=2070',
      registrants: 0,
      isLive:      false,
      category:    safeStr(proposal.topic, 'General'),
    };
    setWebinars(prev => [newWebinar, ...prev]);
    setProposals(prev => prev.filter(p => p.id !== proposal.id));
  }, []);

  const handleReject = useCallback((id) => {
    if (!isMountedRef.current) return;
    setProposals(prev => prev.filter(p => p.id !== id));
  }, []);

  const handleAddSeries = useCallback((newWebinar) => {
    if (!isMountedRef.current || !newWebinar?.id) return;
    setWebinars(prev => [newWebinar, ...prev]);
  }, []);

  const handleCloseProposals = useCallback(() => setShowProposals(false), []);
  const handleCloseAddModal  = useCallback(() => setShowAddModal(false),  []);

  /**
   * Stable total — uses functional form so the value only changes when the
   * registrant counts actually change, not on every array reference swap.
   */
  const totalRegistrants = useMemo(
    () => webinars.reduce((sum, w) => sum + safeNum(w?.registrants), 0),
    [webinars]
  );
  const avgAttendance = 78;

  /* ── Analytics View ── */
  if (view === 'analytics') {
    return (
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-8">
        <div className="flex items-center justify-between">
          <button
            onClick={() => setView('list')}
            className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-text-muted hover:text-accent-gold transition-colors"
          >
            <SafeIcon icon={FiArrowLeft} /> Back to Schedule
          </button>
          <button className="btn-secondary py-2.5 px-6 text-[10px] uppercase tracking-widest flex items-center gap-2">
            <SafeIcon icon={FiDownload} /> Export Dataset
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <AnalyticsStatCard label="Total Reach"     value={totalRegistrants.toLocaleString()} sub="+14.2% Growth"       icon={FiUsers}      color="text-blue-500"      />
          <AnalyticsStatCard label="Engagement Rate" value={`${avgAttendance}%`}               sub="Community Active"     icon={FiActivity}   color="text-green-500"     />
          <AnalyticsStatCard label="Average Rating"  value="4.9/5"                             sub="Based on 420 reviews" icon={FiTrendingUp} color="text-accent-gold"   />
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="bg-white p-8 rounded-[40px] border border-brand-dark shadow-sm">
            <h4 className="text-xl font-serif text-accent-navy mb-8">Content Distribution</h4>
            <div className="flex items-center gap-12">
              <div className="relative w-40 h-40 shrink-0">
                <div className="absolute inset-0 rounded-full border-[16px] border-brand-light" />
                <div className="absolute inset-0 rounded-full border-[16px] border-accent-gold border-t-transparent border-r-transparent -rotate-45" />
                <div className="absolute inset-0 flex items-center justify-center text-center">
                  <div>
                    <p className="text-2xl font-serif text-accent-navy">5</p>
                    <p className="text-[8px] font-black text-text-muted uppercase">Categories</p>
                  </div>
                </div>
              </div>
              <div className="flex-grow space-y-4">
                <ProgressRow label="Wellness & Healing" value={45} color="bg-accent-gold"  />
                <ProgressRow label="Legal & Protection"  value={30} color="bg-accent-navy"  />
                <ProgressRow label="Writing & Legacy"    value={25} color="bg-purple-600"   />
              </div>
            </div>
          </div>
          <div className="bg-white p-8 rounded-[40px] border border-brand-dark shadow-sm">
            <div className="flex justify-between items-center mb-8">
              <h4 className="text-xl font-serif text-accent-navy">Attendee Trends</h4>
              <div className="flex gap-2">
                <div className="flex items-center gap-1.5 text-[9px] font-black uppercase text-text-muted">
                  <div className="w-2 h-2 rounded-full bg-accent-gold" /> Live
                </div>
                <div className="flex items-center gap-1.5 text-[9px] font-black uppercase text-text-muted">
                  <div className="w-2 h-2 rounded-full bg-brand-dark" /> Replay
                </div>
              </div>
            </div>
            <div className="h-40 flex items-end gap-2 px-2">
              {[30, 55, 40, 85, 60, 95, 75, 45, 100, 80].map((h, i) => (
                <div key={i} className="flex-grow flex flex-col justify-end group">
                  <div className="w-full bg-brand-light group-hover:bg-accent-gold/20 transition-colors rounded-t-md relative h-full">
                    <motion.div
                      initial={{ height: 0 }}
                      whileInView={{ height: `${h}%` }}
                      viewport={{ once: true }}
                      transition={{ delay: i * 0.05, duration: 0.5, ease: 'easeOut' }}
                      className="absolute bottom-0 left-0 right-0 bg-accent-navy rounded-t-md"
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </motion.div>
    );
  }

  /* ── List View ── */
  return (
    <div className="space-y-8">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-accent-navy text-white p-8 md:p-10 rounded-[40px] shadow-2xl flex flex-col md:flex-row justify-between items-center gap-8 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 bg-accent-gold/10 rounded-full -mr-32 -mt-32 blur-3xl" />
          <div className="relative z-10">
            <h3 className="text-3xl font-serif mb-2">Webinar Management</h3>
            <p className="text-white/60 font-light max-w-sm">
              Schedule recurring series, manage registrants, and analyze performance.
            </p>
          </div>
          <div className="relative z-10 flex flex-col sm:flex-row gap-4">
            <button
              onClick={() => setView('analytics')}
              className="px-8 py-4 bg-white/5 border border-white/20 rounded-2xl text-white hover:bg-white/10 transition-all flex items-center justify-center gap-3 text-sm font-bold"
            >
              <SafeIcon icon={FiBarChart2} /> Deep Analytics
            </button>
            <button
              onClick={() => setShowAddModal(true)}
              className="btn-accent px-10 py-4 flex items-center justify-center gap-3 text-sm group whitespace-nowrap"
            >
              <SafeIcon icon={FiPlus} className="group-hover:rotate-90 transition-transform" /> New Series
            </button>
          </div>
        </div>

        <div className="bg-white p-8 rounded-[40px] border border-brand-dark shadow-sm flex flex-col justify-between group">
          <div>
            <div className="flex justify-between items-start mb-4">
              <div className="w-12 h-12 bg-accent-gold/10 text-accent-gold rounded-2xl flex items-center justify-center border border-accent-gold/20">
                <SafeIcon icon={FiZap} className="w-6 h-6" />
              </div>
              {proposals.length > 0 && (
                <span className="bg-red-500 text-white text-[9px] font-black px-2 py-1 rounded-full animate-pulse">
                  {proposals.length} NEW
                </span>
              )}
            </div>
            <h4 className="font-serif text-xl text-accent-navy mb-1">Speaker Proposals</h4>
            <p className="text-xs text-text-muted font-light">Review community submissions</p>
          </div>
          <button
            onClick={() => setShowProposals(true)}
            className="w-full mt-6 py-3 bg-brand-light text-accent-navy rounded-xl font-black text-[10px] uppercase tracking-widest hover:bg-accent-navy hover:text-white transition-all shadow-sm"
          >
            Review Applications
          </button>
        </div>
      </div>

      <div className="bg-white rounded-[40px] border border-brand-dark shadow-sm overflow-hidden">
        <div className="p-8 border-b border-brand-light flex justify-between items-center bg-brand-light/30">
          <div>
            <h4 className="text-xl font-serif text-accent-navy">Scheduled Sessions</h4>
            <p className="text-[10px] font-black uppercase tracking-widest text-text-muted">
              {webinars.length > 0
                ? `${webinars.length} session${webinars.length !== 1 ? 's' : ''} on calendar`
                : 'Global Calendar View'}
            </p>
          </div>
          <button className="p-3 bg-white border border-brand-dark rounded-xl text-text-muted hover:text-accent-navy transition-all shadow-sm flex items-center gap-2">
            <SafeIcon icon={FiDownload} />
            <span className="text-[10px] font-black uppercase tracking-widest">Export All Reports</span>
          </button>
        </div>
        <div className="divide-y divide-brand-light">
          {webinars.length === 0
            ? <EmptyWebinarList />
            : webinars.map(w =>
                w?.id ? (
                  <WebinarRow key={w.id} w={w} onViewAnalytics={() => setView('analytics')} />
                ) : null
              )
          }
        </div>
      </div>

      {/* Proposals Modal */}
      <AnimatePresence>
        {showProposals && (
          <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-accent-navy/60 backdrop-blur-md">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="bg-white rounded-[40px] shadow-2xl w-full max-w-4xl overflow-hidden border border-brand-dark flex flex-col max-h-[90vh]"
            >
              <div className="p-8 border-b border-brand-light flex justify-between items-center bg-brand-light/30 shrink-0">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-accent-navy text-white rounded-2xl flex items-center justify-center shadow-lg">
                    <SafeIcon icon={FiZap} className="w-6 h-6 text-accent-gold" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-serif text-accent-navy">Proposed Sessions</h3>
                    <p className="text-[10px] font-black uppercase tracking-widest text-text-muted">
                      Awaiting curation approval
                    </p>
                  </div>
                </div>
                <button
                  onClick={handleCloseProposals}
                  className="p-2 hover:bg-brand-light rounded-full text-text-muted transition-colors"
                >
                  <SafeIcon icon={FiX} className="w-6 h-6" />
                </button>
              </div>
              <div className="flex-grow overflow-y-auto p-8 space-y-6 min-h-0">
                {proposals.length === 0
                  ? <EmptyProposalList />
                  : proposals.map(prop =>
                      prop?.id ? (
                        <ProposalCard
                          key={prop.id}
                          prop={prop}
                          onApprove={handleApprove}
                          onReject={handleReject}
                        />
                      ) : null
                    )
                }
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Add Series Modal */}
      <AnimatePresence>
        {showAddModal && (
          <AddSeriesModal
            onClose={handleCloseAddModal}
            onSave={handleAddSeries}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────────────────
   EXPORTED COMPONENT — wrapped in ErrorBoundary so any uncaught render-
   phase exception in AdminWebinarTabInner or its children is caught here
   rather than propagating up to crash the entire Dashboard.
───────────────────────────────────────────────────────────────────────── */
const AdminWebinarTab = () => (
  <ErrorBoundary
    fallback={(err) => (
      <div className="flex flex-col items-center justify-center min-h-[400px] px-6 text-center">
        <div className="w-20 h-20 rounded-full bg-red-50 flex items-center justify-center mb-6">
          <span className="text-3xl">⚠️</span>
        </div>
        <h2 className="text-2xl font-serif text-accent-navy mb-3">Webinar tab unavailable</h2>
        <p className="text-text-muted text-sm max-w-sm mb-2">
          An unexpected error occurred while loading the webinar management panel.
        </p>
        {process.env.NODE_ENV !== 'production' && err?.message && (
          <pre className="text-xs text-red-400 bg-red-50 rounded-xl px-4 py-3 mt-2 max-w-md text-left overflow-auto">
            {err.message}
          </pre>
        )}
      </div>
    )}
  >
    <AdminWebinarTabInner />
  </ErrorBoundary>
);

export default AdminWebinarTab;